import React, { useLayoutEffect, useRef } from 'react';
import { HtmlTable } from '../../../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { StyledColgroup, StyledTable } from './TableContent.styles';
import { TableHeader } from './components/TableHeader/TableHeader';
import { EstateTableRow } from './components/TableRow/components/EstateTableRow/EstateTableRow';
import { LiquidityTableRow } from './components/TableRow/components/LiquidityTableRow/LiquidityTableRow';
import { Body, CollapsableHeadersData, FilteredCategoryAndHeaders } from '../../FlowTable.meta';
import { GroupTotal } from './components/TableRow/components/GroupTotal/GroupTotal';

interface Props {
  body: Body;
  collapsableHeaderIds: string[];
  filteredCategoriesAndHeaders: FilteredCategoryAndHeaders[];
  hasHeaderSelected(id: number): boolean;
  hasShowCollapsed(id: number): boolean;
  setTableHeight: (value: number) => void;
  toggleShowCollapsed(id: number): void;
  collapsableHeadersData: CollapsableHeadersData;
}

export const TableContent = ({
  body,
  collapsableHeaderIds,
  filteredCategoriesAndHeaders,
  hasHeaderSelected,
  hasShowCollapsed,
  setTableHeight,
  toggleShowCollapsed,
  collapsableHeadersData,
}: Props) => {
  const ref = useRef<HTMLTableElement>(null);

  useLayoutEffect(() => {
    if (ref?.current) {
      const { height } = ref.current.getBoundingClientRect();

      setTableHeight(height);
    }
  });

  return (
    <HtmlTable variant="embedded" columnSpacing="small" fixedHead overflowY="hidden" ref={ref}>
      <StyledTable>
        <StyledColgroup>
          {filteredCategoriesAndHeaders.map(({ name, filteredHeaders }) => {
            if (filteredHeaders.length === 0) {
              return null;
            }

            return (
              <React.Fragment key={name}>
                <col span={filteredHeaders.length} />
              </React.Fragment>
            );
          })}
        </StyledColgroup>

        <TableHeader
          hasShowCollapsed={hasShowCollapsed}
          toggleShowCollapsed={toggleShowCollapsed}
          filteredCategoriesAndHeaders={filteredCategoriesAndHeaders}
          collapsableHeadersData={collapsableHeadersData}
        />

        <tbody>
          {Object.keys(body).map(key => {
            const bodyData = body[key];

            if (key !== 'totals') {
              if (!bodyData.totals.some(({ value }) => value !== null)) {
                return null;
              }

              return (
                <React.Fragment key={key}>
                  <EstateTableRow name={bodyData.name} />

                  {bodyData.liquidAssets && (
                    <LiquidityTableRow
                      liquidityData={bodyData.liquidAssets}
                      tableRef={ref}
                      setTableHeight={setTableHeight}
                      collapsableHeaderIds={collapsableHeaderIds}
                      hasHeaderSelected={hasHeaderSelected}
                      hasShowCollapsed={hasShowCollapsed}
                    />
                  )}

                  {bodyData.illiquidAssets && (
                    <LiquidityTableRow
                      liquidityData={bodyData.illiquidAssets}
                      tableRef={ref}
                      setTableHeight={setTableHeight}
                      collapsableHeaderIds={collapsableHeaderIds}
                      hasHeaderSelected={hasHeaderSelected}
                      hasShowCollapsed={hasShowCollapsed}
                    />
                  )}

                  <GroupTotal
                    name={`${bodyData.name} Total`}
                    totals={bodyData.totals}
                    hasHeaderSelected={hasHeaderSelected}
                    collapsableHeaderIds={collapsableHeaderIds}
                    hasShowCollapsed={hasShowCollapsed}
                  />
                </React.Fragment>
              );
            }

            return (
              <GroupTotal
                key={key}
                name="Total Resources"
                totals={bodyData}
                hasHeaderSelected={hasHeaderSelected}
                collapsableHeaderIds={collapsableHeaderIds}
                hasShowCollapsed={hasShowCollapsed}
              />
            );
          })}
        </tbody>
      </StyledTable>
    </HtmlTable>
  );
};
